/**
 * Blotter — a generic, config-driven data table.
 *
 * One reusable table shell that owns **search, filters, sort, pagination, CSV export, the
 * sticky-header table, empty/loading states, and the footer pager**. You don't render any of
 * that yourself — you describe *your* surface with a `BlotterConfig<T>` and render:
 *
 *   <Blotter config={myConfig} />
 *
 * `T` is your row type (e.g. `User`, `Organization`). Everything the blotter shows or does
 * for a row is derived from the config.
 *
 * ── Mental model ────────────────────────────────────────────────────────────────────────
 * The blotter owns the *query state* (search text, applied filters, sort key/dir, current
 * page) and hands it to your `useData` hook every render. Your hook fetches the matching
 * page from the backend and returns it. So **sorting, filtering, and pagination are all
 * server-side** — the blotter never re-sorts or slices rows client-side; it renders exactly
 * what `useData` returns, in the order the backend gave them. Search is debounced 300ms, and
 * any change to search / filters / sort resets to page 1. Default sort = the first column;
 * page size = `PAGE_LIMIT`.
 *
 * ── Minimal usage ───────────────────────────────────────────────────────────────────────
 *   const config: BlotterConfig<User> = {
 *     title: "Users",
 *     searchPlaceholder: "Search users",
 *     itemNoun: "user",                       // "3 users · Page 1 of 2", "No users match…"
 *     rowKey: (u) => u.id,                     // stable unique key per row
 *     filterFields: [{ id: "status", label: "Status", options: STATUS_OPTIONS }],
 *     useData: useUsersData,                   // a HOOK — see the rule below
 *     columns: [
 *       { key: "name", label: "Name", sortable: true, sortValue: (u) => u.name,
 *         render: (u) => <strong>{u.name}</strong> },
 *       { key: "email", label: "Email", sortable: true, sortValue: (u) => u.email },
 *     ],
 *     exportFilename: "users.csv",
 *     exportFetch: (q) => fetchUsers({ ...q }), // full filtered set for CSV (optional)
 *     leadingCell: (u) => <ImpersonateButton user={u} />, // optional cell before col 1
 *     headerAction: can("create","USER") ? <InviteButton/> : undefined, // optional header slot
 *   };
 *   return <Blotter config={config} />;
 *
 * ── What goes in BlotterConfig (see the interface below for per-field docs) ──────────────
 *   Required:
 *     title, searchPlaceholder      – header text
 *     rowKey                        – (row) => unique key
 *     filterFields                  – FilterField[] driving the BlotterFilterBar
 *     columns                       – BlotterColumn<T>[]: key/label + optional sortable,
 *                                     render, sortValue, exportValue, cellStyle, visibleWhen
 *     useData                       – the data hook (query state in → {data,total,totalPages,isLoading})
 *     exportFilename                – filename for the CSV download
 *   Optional:
 *     leadingCell                   – a cell before the first column (icon/action per row)
 *     headerAction                  – a node in the header row, right of Export (gate it yourself)
 *     exportFetch                   – paginated fetch of the FULL filtered set for CSV; omit to
 *                                     export only the currently loaded page (capped ~100 pages/10k rows)
 *     exportDownload                – server-built CSV download; replaces exportFetch + the client build
 *     minWidth (default 760)        – table min width before horizontal scroll
 *     itemNoun (default "row")      – singular noun for counts/empty state; the plural is derived
 *                                     ("entry" → "entries"), override with itemNounPlural if irregular
 *
 * ── The one rule that bites: hooks vs. renderers ────────────────────────────────────────
 *   • `useData` IS A HOOK — the blotter calls it once, unconditionally, every render. It must
 *     obey the Rules of Hooks (stable, top-level; you may call useQuery etc. inside it).
 *   • `render`, `leadingCell`, `sortValue`, `exportValue`, `visibleWhen` are PLAIN RENDERERS
 *     called per row/column. They must NOT call hooks. If a cell needs hook data (permissions,
 *     mutations, navigation), resolve it in the *parent* component and let the renderer close
 *     over the value — e.g. `const { can } = usePermissions(); … leadingCell: can("start","IMPERSONATION") ? … : undefined`.
 *
 * A column with `sortable: true` must provide `sortValue` (used as the sort payload and as the
 * CSV fallback). `visibleWhen(filters)` lets a column show only under certain active filters
 * (e.g. a GFG-only column that appears when the "GFG User" filter is on).
 */

/**
 * Opt-in extensions — `emptyMessage`, `csvColumns`, `rowHover`, `pageResetKey`, `rowClassName`.
 *
 * All five are optional, and **each default reproduces the behaviour this component had before
 * they existed**: the empty state still reads "No {nounPlural} match your search.", the CSV is
 * still derived from the visible columns' labels, every row still carries exactly the
 * `blotter-row` class (so `.blotter-row:hover` still applies), and nothing resets the page but
 * search / filters / sort. A blotter that passes none of them is byte-for-byte the old one.
 */
import {
  ArrowDownward,
  ArrowUpward,
  FileDownloadOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  UnfoldMore,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";
import BlotterFilterBar, {
  type AppliedFilter,
  type FilterField,
} from "@/components/BlotterFilterBar";
import { PAGE_LIMIT } from "@/constants/users";
import { buildCsv, type CsvColumn, downloadCsv } from "@/lib/csv";

/** Default CSV columns: every column not marked `exportable: false`, valued by exportValue else sortValue. */
export function blotterCsvColumns<T>(columns: BlotterColumn<T>[]): CsvColumn<T>[] {
  return columns
    .filter((c) => c.exportable !== false)
    .map((c) => ({
      label: c.label,
      value: (row: T) => c.exportValue?.(row) ?? String(c.sortValue?.(row) ?? ""),
    }));
}

/** One column: a header + how to render/sort/export its cell for a row of type T. */
export interface BlotterColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number;
  /** Plain-text value for CSV export; defaults to `sortValue`. */
  exportValue?: (row: T) => string;
  /** `false` keeps the column out of the CSV (e.g. a trailing actions column). */
  exportable?: boolean;
  cellStyle?: CSSProperties;
  /**
   * Overrides on the column's `<th>`, merged over the shared header style. Exists so a column that
   * moves its cells off the default left alignment can move its heading with them: the two are one
   * decision, and setting only `cellStyle` detaches a column's values from their own label.
   */
  headerStyle?: CSSProperties;
  /** Show this column only when the predicate passes for the active filters (e.g. a GFG-only column). */
  visibleWhen?: (filters: AppliedFilter[]) => boolean;
}

/** What a config's data hook must return (mirrors a React Query result, mapped). */
export interface BlotterData<T> {
  data: T[];
  total: number;
  totalPages: number;
  isLoading: boolean;
}

/**
 * BlotterConfig — everything specific to one blotter surface. Pass it to <Blotter/>
 * and the generic component handles search / filters / sort / pagination / export /
 * the table shell. `useData` is a hook (called once per render); `leadingCell` and
 * column `render`s are plain renderers (they may close over hooks resolved by the
 * caller, but must not call hooks themselves).
 */
export interface BlotterConfig<T> {
  /** Omit when the surrounding page already names the surface — two headings on one screen. */
  title?: string;
  searchPlaceholder: string;
  filterFields: FilterField[];
  /** Opt in to the Is / Is not + search + Cancel/Apply filter panel. Defaults to false. */
  filterPanel?: boolean;
  columns: BlotterColumn<T>[];
  rowKey: (row: T) => number | string;
  useData: (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }) => BlotterData<T>;
  /** Optional cell before the first column (e.g. an impersonate button). */
  leadingCell?: (row: T) => ReactNode;
  /** Optional action rendered in the header row alongside Export (e.g. Invite user). */
  headerAction?: ReactNode;
  /** Which side of the Export button `headerAction` sits on. Defaults to "end" (its right). */
  headerActionPlacement?: "start" | "end";
  exportFilename: string;
  /** Overrides the export button's label. Defaults to "Export CSV", which is what every blotter
   *  uses — override only for a genuinely different kind of download. */
  exportLabel?: string;
  /**
   * Paginated fetch of the FULL filtered set for CSV export (search + filters,
   * all pages). Omit to export just the currently loaded page.
   *
   * If it rejects, the blotter stops the walk and writes **no file** — report the failure
   * from inside your `exportFetch` (that is where the copy lives) and then let it reject.
   * Resolving with empty data instead makes the blotter download the pages it already had,
   * which reads as a complete export. Screens with no error surface of their own can leave the
   * reporting to `onExportError` below, whose default keeps the rejection observable.
   */
  exportFetch?: (q: {
    search: string;
    filters: AppliedFilter[];
    page: number;
    limit: number;
  }) => Promise<{ data: T[]; totalPages: number }>;
  /**
   * Called with the rejection when a page of `exportFetch` fails, just before the export is
   * abandoned. Pass it when the screen owns somewhere to say so (a toast, an inline banner), or
   * pass a no-op when the screen's own `exportFetch` already reported the failure — so one
   * failure is announced once.
   *
   * Omit it and the blotter re-raises the error in a fresh task, so it reaches `window.onerror`
   * and the error reporter exactly as the unhandled rejection did before the walk was guarded.
   * Most blotters have no error surface of their own, and a swallowed error on those is a click
   * where nothing happens at all: no file, no message, no console entry, no telemetry. Aborting
   * the download is the blotter's job; staying observable is not optional.
   */
  onExportError?: (error: unknown) => void;
  /**
   * Server-side CSV download, used INSTEAD of `exportFetch` + the client CSV build when set: the
   * blotter awaits it once with the current search, filters and sort (no page walk, no file built
   * here) and the callee saves the file. A rejection goes to `onExportError` like a failed page.
   */
  exportDownload?: (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
  }) => Promise<void>;
  minWidth?: number;
  itemNoun?: string;
  /** Override when the plural isn't regular — "person" → "people". Otherwise derived. */
  itemNounPlural?: string;
  /** Rows per page. Defaults to PAGE_LIMIT (20). */
  pageSize?: number;
  /** Initial sort column key. Defaults to the first column. */
  defaultSortKey?: string;
  /** Initial sort direction. Defaults to "asc". */
  defaultSortDir?: "asc" | "desc";

  // ── Opt-in extensions. Omit them all and the blotter behaves exactly as it always has. ──

  /** Replaces the empty-state text. Defaults to "No {nounPlural} match your search.". */
  emptyMessage?: string;
  /**
   * Overrides the exported CSV columns. Defaults to one column per *visible* column, labelled
   * with its header and valued by `exportValue` (falling back to `sortValue`).
   */
  csvColumns?: CsvColumn<T>[];
  /** Set to false to drop the row hover highlight. Defaults to true (hover on). */
  rowHover?: boolean;
  /**
   * Returns to page 1 whenever this value changes — e.g. a Time Window switch that shrinks the
   * result set would otherwise strand the user on a page that no longer exists. Leave undefined
   * (the default) for no page resetting at all; it never fires on first mount. Clearing the key
   * back to undefined holds the page (the result set only widens) but is still recorded, so
   * re-selecting the value the user just cleared resets as it should.
   */
  pageResetKey?: string | number;
  /**
   * Extra class(es) appended to the row's `blotter-row`, per row. `blotter-row` always stays.
   * A class that paints the row should also set `--blotter-row-bg` to the same colour, so the
   * highlight survives the cursor under `rowHover={false}` (see index.css).
   */
  rowClassName?: (row: T) => string | undefined;
}

// Matches the design's `.history-table th` (GfG Admin view): wax header band, bold
// rodeo-black column headings, 1px tracking.
const th: CSSProperties = {
  textAlign: "left",
  padding: "12px 16px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "var(--color-rodeo-black)",
  whiteSpace: "nowrap",
  userSelect: "none",
  // Pin the header while the body scrolls. Opaque bg so rows don't show through;
  // box-shadow supplies the bottom border (collapsed borders don't stick).
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "var(--color-wax)",
  boxShadow: "inset 0 -1px 0 var(--color-smoke)",
};

// Matches the design's `.history-table td`.
const td: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

/**
 * English plural for the count line. Naively appending "s" gave "6 entrys"; this covers the two
 * rules that actually bite — consonant + y takes -ies, and a sibilant ending takes -es. Anything
 * irregular sets `itemNounPlural` rather than growing this.
 */
function pluralise(noun: string): string {
  if (/[^aeiou]y$/i.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(noun)) return `${noun}es`;
  return `${noun}s`;
}

export default function Blotter<T>({ config }: { config: BlotterConfig<T> }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AppliedFilter[]>([]);
  const [sortKey, setSortKey] = useState<string>(
    config.defaultSortKey ?? config.columns[0]?.key ?? "",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">(config.defaultSortDir ?? "asc");
  const [exporting, setExporting] = useState(false);

  // Columns can opt into visibility based on the active filters (e.g. a GFG-only column).
  const columns = config.columns.filter((c) => !c.visibleWhen || c.visibleWhen(filters));

  // Debounce the search input; a new query resets to page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Opt-in: return to page 1 when `pageResetKey` changes. Derived *during* render rather than in
  // an effect — an effect resets after commit, so `useData` below would already have fired one
  // query for the stale page (empty table, then a second query once the reset lands). Setting
  // state during render makes React re-run this render before committing anything, so the query
  // only ever runs with the page the user ends up on. The state seeds from the first render, so
  // mounting never resets, and a key that is always undefined never differs from its seed —
  // that config stays completely inert.
  const [lastResetKey, setLastResetKey] = useState(config.pageResetKey);
  if (lastResetKey !== config.pageResetKey) {
    // Record the key even when it is undefined (the control was cleared). Skipping this is what
    // made the same key coming back look unchanged, leaving the user on a page that no longer
    // exists. Clearing itself doesn't reset: dropping the key only ever widens the result set.
    setLastResetKey(config.pageResetKey);
    if (config.pageResetKey !== undefined) setPage(1);
  }

  // Sort is applied server-side (over the full filtered set, not just this page),
  // so `rows` is the data exactly as the backend ordered it.
  const pageLimit = config.pageSize ?? PAGE_LIMIT;
  const { data, total, totalPages, isLoading } = config.useData({
    search,
    filters,
    sortBy: sortKey,
    sortDir,
    page,
    limit: pageLimit,
  });

  const rows = data;

  function toggleSort(key: string) {
    if (key === sortKey) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        // Third click: no "unsorted" state exists server-side, so fall back to the default sort.
        setSortKey(config.defaultSortKey ?? config.columns[0]?.key ?? "");
        setSortDir(config.defaultSortDir ?? "asc");
      }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  }

  function handleFiltersChange(next: AppliedFilter[]) {
    setFilters(next);
    setPage(1);
  }

  // CSV cells come from the columns: exportValue if given, else the sort value.
  // `config.csvColumns` wins when supplied; otherwise this derivation is unchanged.
  const csvColumns: CsvColumn<T>[] = config.csvColumns ?? blotterCsvColumns(columns);

  /**
   * Reports a failed export page. `config.onExportError` wins when the screen supplied one;
   * otherwise the error is re-raised in a fresh task, which is what puts it back in front of
   * `window.onerror` / error reporting. Re-raising from a timeout instead of from here keeps
   * the walk on its normal path, so `handleExport`'s `finally` still clears the exporting
   * state and the button never sticks on "Exporting…".
   */
  function reportExportError(error: unknown): void {
    if (config.onExportError) {
      config.onExportError(error);
      return;
    }
    setTimeout(() => {
      throw error;
    });
  }

  /**
   * Walks every page of the filtered set for the CSV.
   *
   * Resolves to `null` when a page fetch rejects: the walk stops at the page that failed and
   * the caller must not write a file. A CSV holding only the pages that happened to succeed
   * looks complete and isn't, and an admin acting on silently missing rows is worse off than
   * one whose export failed. The catch stops the download only — it does not consume the
   * error: `reportExportError` hands it to the screen, or re-raises it, so a failed export is
   * never a click where nothing happens.
   *
   * Only the fetch itself is guarded, so nothing else in the walk can hide an error.
   */
  async function fetchExportRows(
    exportFetch: NonNullable<BlotterConfig<T>["exportFetch"]>,
  ): Promise<T[] | null> {
    const all: T[] = [];
    let p = 1;
    let tp = 1;
    do {
      let res: Awaited<ReturnType<typeof exportFetch>>;
      try {
        res = await exportFetch({ search, filters, page: p, limit: 100 });
      } catch (error: unknown) {
        reportExportError(error);
        return null;
      }
      all.push(...res.data);
      tp = res.totalPages;
      p += 1;
    } while (p <= tp && p <= 100); // safety cap: 100 pages (~10k rows)
    return all;
  }

  async function handleExport() {
    setExporting(true);
    try {
      if (config.exportDownload) {
        try {
          await config.exportDownload({ search, filters, sortBy: sortKey, sortDir });
        } catch (error) {
          reportExportError(error);
        }
        return;
      }
      let out = rows;
      // Prefer the full filtered set (all pages) when the config can fetch it.
      if (config.exportFetch) {
        const all = await fetchExportRows(config.exportFetch);
        // A page fetch failed and has already been reported by `fetchExportRows`: no file at
        // all beats one that is quietly missing rows. The `finally` below still ends the
        // exporting state, so the button returns to normal.
        if (all === null) return;
        out = all;
      }
      downloadCsv(config.exportFilename, buildCsv(csvColumns, out));
    } finally {
      setExporting(false);
    }
  }

  const colCount = columns.length + (config.leadingCell ? 1 : 0);
  const noun = config.itemNoun ?? "row";
  const nounPlural = config.itemNounPlural ?? pluralise(noun);

  /** "blotter-row" plus any opt-in modifiers. With both props omitted it is exactly the former. */
  function rowClasses(row: T): string {
    const modifiers = [
      config.rowHover === false ? "blotter-row--no-hover" : "",
      config.rowClassName?.(row) ?? "",
    ].filter(Boolean);
    return modifiers.length > 0 ? `blotter-row ${modifiers.join(" ")}` : "blotter-row";
  }

  return (
    <Box>
      {config.title && (
        <h2
          style={{
            fontFamily: "var(--font-crimson)",
            fontSize: 20,
            fontWeight: 400,
            color: "var(--color-rodeo-black)",
            margin: "0 0 16px",
          }}
        >
          {config.title}
        </h2>
      )}

      {/* Wraps rather than crushing: on a narrow screen the filter bar and the buttons cannot
          share a line, and flex would otherwise shrink them past their own content. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <BlotterFilterBar
          placeholder={config.searchPlaceholder}
          search={searchInput}
          onSearchChange={setSearchInput}
          fields={config.filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          panel={config.filterPanel}
        />
        {config.headerActionPlacement === "start" && config.headerAction}
        <Button
          onClick={handleExport}
          disabled={exporting}
          startIcon={<FileDownloadOutlined />}
          sx={{
            border: "1px solid var(--color-dark-dust)",
            borderRadius: "var(--radius-btn)",
            color: "var(--color-dark-dust)",
            height: 48,
            px: 2,
            fontFamily: "var(--font-lato)",
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: "0.08em",
            whiteSpace: "nowrap",
            // Without this, flex shrinks the button below its label and the icon and text spill
            // outside the border, over whatever sits next to it.
            flexShrink: 0,
          }}
        >
          {exporting ? "Exporting…" : (config.exportLabel ?? "Export CSV")}
        </Button>
        {config.headerActionPlacement !== "start" && config.headerAction}
      </Box>

      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderBottom: "none",
          borderRadius: "var(--radius-card) var(--radius-card) 0 0",
          bgcolor: "background.paper",
          px: 2,
          py: 1.25,
          fontSize: 13,
          color: "var(--color-graphite)",
        }}
      >
        {total.toLocaleString()} {total === 1 ? noun : nounPlural}
      </Box>

      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderRadius: "0 0 var(--radius-card) var(--radius-card)",
          bgcolor: "background.paper",
          overflow: "auto",
          maxHeight: "calc(100vh - 300px)",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", minWidth: config.minWidth ?? 760 }}
        >
          <thead>
            <tr>
              {config.leadingCell && <th style={{ ...th, width: 44 }} />}
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{ ...th, ...c.headerStyle, cursor: c.sortable ? "pointer" : "default" }}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                    {c.label}
                    {c.sortable &&
                      (sortKey === c.key ? (
                        // Distinct glyphs, not one icon rotated — direction must read at a glance.
                        sortDir === "asc" ? (
                          <ArrowUpward sx={{ fontSize: 14, opacity: 0.9 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 14, opacity: 0.9 }} />
                        )
                      ) : (
                        <UnfoldMore sx={{ fontSize: 14, opacity: 0.35 }} />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={colCount} style={{ ...td, textAlign: "center", padding: 40 }}>
                  <CircularProgress aria-label="Loading rows" size={22} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  style={{
                    ...td,
                    textAlign: "center",
                    padding: 40,
                    color: "var(--color-graphite)",
                  }}
                >
                  {config.emptyMessage ?? <>No {nounPlural} match your search.</>}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={config.rowKey(row)} className={rowClasses(row)}>
                  {config.leadingCell && (
                    <td style={{ ...td, width: 44 }}>{config.leadingCell(row)}</td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} style={c.cellStyle ?? td}>
                      {c.render
                        ? c.render(row)
                        : String((row as Record<string, unknown>)[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 2 }}
      >
        <Box sx={{ fontSize: 13, color: "var(--color-graphite)", mr: 1 }}>
          {total === 0
            ? `0 ${nounPlural}`
            : (() => {
                const start = (page - 1) * pageLimit + 1;
                const end = Math.min(page * pageLimit, total);
                return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`;
              })()}
        </Box>
        <IconButton
          size="small"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          aria-label="Previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          aria-label="Next page"
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Box>
  );
}
